import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { TEMPLATE_COLOR_VAR_PREFIX, TEMPLATE_FONT_VAR_PREFIX } from '@lumen5/luminary';

import { getColorLabel } from '../utils';

import { SetSceneDurationTool } from './SetSceneDurationTool';
import { WriteHtmlAssetTool } from './WriteHtmlAssetTool';
import { getSceneDescriptions, getVideoDescription } from '../video-state/serialization';
import { formatAsset } from '../video-state/serialization/assetFormatters';
import type {
    AssetDescription,
    SceneDescription,
    TextAssetDescription,
} from '../video-state/types';

import type { WebsiteAnalysisResult } from 'js/src/agent/codeo/website-brand/types';
import { getBrandKitColorsInHex } from 'js/src/data/brand-kits/brand-kits-selectors';
import { selectSelectedBrand } from 'js/src/data/codeo';
import { getBrandKitId } from 'js/src/data/video/video-selectors';
import { intToHex } from 'js/src/libs/color-utils';
import type { RootState } from 'js/src/libs/RootState';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import { translateTranscriptTimeToSceneGroupTime } from 'js/src/luminary/luminary-selectors';
import { isVoicedrivenScene } from 'js/src/luminary/voicedriven-scenes';

/**
 * Builds the scene-scoped context injected into the HTML-design agent each turn: the video's frame
 * dimensions, the scene the graphic lives on (its other elements), the narration spoken over it,
 * the neighboring scenes, the real template palette as hex, the fonts, and the customer's brand
 * colors. This replaces the generic whole-video state channel (which is coarse and never surfaces
 * the palette as usable values) with exactly what a graphic designing *for one scene* needs.
 */
export function buildHtmlDesignContext(
    luminary: Luminary,
    sceneId: string,
    assetId: string,
    state: RootState,
): string {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return '';
    }

    const sections: string[] = [];

    const video = getVideoDescription(luminary);
    sections.push(
        `## The video\nresolution: ${video.resolution} (${video.aspectRatio}) — author your composition in this coordinate space`,
    );

    sections.push(formatSceneSection(scene, luminary, assetId, state));

    const narration = formatSceneNarration(scene);
    if (narration) {
        sections.push(narration);
    }

    const neighbors = formatNeighborSection(luminary, scene, state);
    if (neighbors) {
        sections.push(neighbors);
    }

    const palette = formatPalette(templateColorInts(luminary));
    if (palette.length) {
        sections.push(
            `## Palette — injected as the CSS variables you reference (\`var(${TEMPLATE_COLOR_VAR_PREFIX}N)\`)\n${palette.join('\n')}`,
        );
    }

    const fonts = formatFonts(luminary.template?.fonts ?? []);
    if (fonts.length) {
        sections.push(
            `## Fonts — injected as the CSS variables you reference (\`var(${TEMPLATE_FONT_VAR_PREFIX}N)\`)\n${fonts.join('\n')}`,
        );
    }

    const brand = formatBrandSection(state);
    if (brand) {
        sections.push(brand);
    }

    const codeoBrand = formatCodeoBrandSection(state);
    if (codeoBrand) {
        sections.push(codeoBrand);
    }

    return sections.join('\n\n');
}

/**
 * The full context an html-design turn runs on: the scene-scoped context plus the composition being
 * edited. Shared rather than inlined into one caller, because `edit_html_asset` matches its `old`
 * substrings against the composition the system prompt promises "at the start of each turn" — anyone
 * driving the design agent who injects only the scene context leaves it editing a document it cannot
 * see.
 */
export function buildHtmlDesignTurnContext(
    luminary: Luminary,
    sceneId: string,
    assetId: string,
    state: RootState,
): string {
    const asset = luminary.getSceneById(sceneId)?.getAssetById(assetId) as HtmlAsset | undefined;
    const currentHtml = asset?.html ?? '';
    const sceneContext = buildHtmlDesignContext(luminary, sceneId, assetId, state);
    const designSection = currentHtml
        ? `The current composition HTML for the design you are editing is:\n\n${currentHtml}`
        : `The design is currently empty. Author the first version with ${WriteHtmlAssetTool.toolName}.`;
    return sceneContext ? `${sceneContext}\n\n${designSection}` : designSection;
}

/** Template palette colors as 24-bit ints, in the order they're injected as `--l5-color-0..N`. */
function templateColorInts(luminary: Luminary): number[] {
    return (luminary.template?.colors ?? []).map((templateColor) => templateColor.color);
}

/**
 * Format the palette as `--l5-color-N: #hex (name)` lines, dropping the name when the color isn't a
 * close match to a named color (see getColorLabel). Pure over an int array (no Luminary instance) so
 * it's unit-testable; hexes match the values Luminary injects into the composition.
 */
export function formatPalette(colors: number[]): string[] {
    return colors.map((color, index) => {
        const label = getColorLabel(color);
        const value = label ? `${intToHex(color)} (${label})` : intToHex(color);
        return `${TEMPLATE_COLOR_VAR_PREFIX}${index}: ${value}`;
    });
}

/**
 * Format the template fonts as `--l5-font-N: <label>` lines, indexed as Luminary injects them. The
 * font is referenced in the composition by that variable, not by name; the label is a readable hint
 * (from the font's filename) so the agent can pick the right slot. Pure over the url array.
 */
export function formatFonts(fontUrls: string[]): string[] {
    return fontUrls.map(
        (url, index) => `${TEMPLATE_FONT_VAR_PREFIX}${index}: ${fontLabelFromUrl(url)}`,
    );
}

/** A readable font name from its URL — the filename without directory, extension or separators. */
function fontLabelFromUrl(url: string): string {
    const filename = url.split(/[?#]/)[0].split('/').pop() ?? url;
    return (
        filename
            .replace(/\.[^.]+$/, '')
            .replace(/[-_]+/g, ' ')
            .trim() || url
    );
}

/**
 * Drop the asset being edited from a scene's asset list — the agent already receives that asset's
 * composition HTML separately, so listing it again as an "other element" is noise. Matched by name
 * (the description carries no id), which is safe because Luminary asset names are unique per scene.
 * Pure over the description list so it's unit-testable without a Luminary instance.
 */
export function excludeEditedAsset(
    assets: AssetDescription[],
    editedAssetName: string | undefined,
): AssetDescription[] {
    return assets.filter((asset) => asset.name !== editedAssetName);
}

function formatSceneSection(
    scene: Scene,
    luminary: Luminary,
    assetId: string,
    state: RootState,
): string {
    const description = getSceneDescriptions(scene, luminary, state);
    const others = excludeEditedAsset(description.assets, scene.getAssetById(assetId)?.name).filter(
        isVisibleAsset,
    );

    const header = `## The scene your graphic is on (scene index ${description.globalIndex} of ${luminary.scenes.length})\nscene group index ${description.sceneGroupIndex}, design ${description.design}, duration ${description.duration}`;
    const regime = formatTimingRegime(scene);

    if (!others.length) {
        return `${header}\n${regime}\n(no other elements on this scene)`;
    }
    const assetsText = annotatePaletteVars(
        others
            .map((asset) => formatAsset(asset))
            .join('')
            .trimEnd(),
        paletteVarByColorHex(luminary),
    );
    return `${header}\n${regime}\nOther elements already on this scene:\n${assetsText}`;
}

function formatDurationSeconds(durationMs: number): string {
    return `${Number((durationMs / 1000).toFixed(2))}s`;
}

/**
 * States the scene's timing regime and whether its duration is the agent's to change — the primary
 * signal that keeps the agent from fighting the timing. A voice-driven scene is locked to its
 * voiceover (design to fit); a text-on-media scene's duration is the designer's to set via
 * set_scene_duration. Uses the shared isVoicedrivenScene predicate so this and the tool's guard agree.
 */
export function formatTimingRegime(scene: Scene): string {
    const seconds = formatDurationSeconds(scene.duration);
    if (isVoicedrivenScene(scene)) {
        return `This scene's duration is fixed at ${seconds} by its voiceover. Design your graphic to fit it — you can't change the timing.`;
    }
    return `This scene has no voiceover, so its duration is yours to set to fit your content (currently ${seconds}). Use ${SetSceneDurationTool.toolName} once you know how long your graphic needs.`;
}

/** A narration word placed within its scene, as start/end scene progress (0–1). */
export interface TimedWord {
    word: string;
    startProgress: number;
    endProgress: number;
}

/** The words spanning a captured frame, with the one being spoken at that instant marked. */
export interface NarrationWindow {
    /** The words spanning the frame, by sequence order, boundaries inclusive. */
    words: TimedWord[];
    /** Index within `words` of the word being spoken at the frame, or -1 in a silent gap. */
    activeIndex: number;
}

/**
 * Describe a scene design's structure — its asset slots — for the design-browsing tools, in the same
 * asset vocabulary the agent reads for its own scene. The palette map comes from `luminary` (the
 * template's, so `var(--l5-color-N)` references resolve). Delegates to {@link formatDesignSlots}.
 */
export function formatSceneDesignStructure(
    description: SceneDescription,
    luminary: Luminary,
): string {
    return formatDesignSlots(description.assets, paletteVarByColorHex(luminary));
}

/**
 * Format a design's asset slots, palette-annotated. Unlike the agent's own-scene view this keeps
 * empty text slots — an unfilled headline/body slot *is* the design's layout — and only drops
 * zero-size assets, which never render. Pure over its inputs so it's unit-testable without Luminary.
 */
export function formatDesignSlots(
    assets: AssetDescription[],
    varByColorHex: Map<string, string>,
): string {
    const slots = assets.filter(hasRenderableBounds);
    if (!slots.length) {
        return 'Design structure: no visible slots.';
    }
    const slotsText = annotatePaletteVars(
        slots
            .map((asset) => formatAsset(asset))
            .join('')
            .trimEnd(),
        varByColorHex,
    );
    return `Design structure — asset slots:\n${slotsText}`;
}

/**
 * The current scene's voiceover narration, tagged with when each word is spoken so the agent can
 * sync its timeline to the speech. Timings are the word's position as scene progress (0–100%) —
 * the unit the agent authors in, since the host maps its timeline across the scene. Returns '' for
 * a scene with no narration (e.g. text-on-media) so the section is omitted; falls back to plain
 * text when the scene isn't voicedriven and word timings can't be mapped.
 */
export function formatSceneNarration(scene: Scene): string {
    const transcript = scene.extractTranscript();
    if (!transcript.text.trim()) {
        return '';
    }
    const timed = timeNarrationWords(scene, transcript.words);
    return timed ? formatTimedNarration(timed) : formatNarration(transcript.text);
}

/**
 * Map each narration word's start and end onto its position within the scene, as progress (0–1).
 * Words arrive in transcript (source-audio) time; convert to scene-group time — which is cut-aware —
 * then rebase against the scene's own window. Returns null when the scene group has no playback
 * regions to map against, so the caller can fall back to plain text.
 */
export function timeNarrationWords(
    scene: Scene,
    words: ReadonlyArray<{ word: string; startTime: number; endTime: number }>,
): TimedWord[] | null {
    const sceneGroup = scene.sceneGroup;
    const regions = sceneGroup?.voicedrivenAssetPlaybackRegions;
    const baseDuration = sceneGroup?.voicedrivenAssetDuration;
    if (!regions?.length || !baseDuration || scene.duration <= 0) {
        return null;
    }
    const sceneStart = scene.startTimeRelativeToSceneGroup;
    const toProgress = (transcriptTime: number): number => {
        const sceneGroupTime = translateTranscriptTimeToSceneGroupTime(
            transcriptTime,
            regions,
            baseDuration,
        );
        const progress = (sceneGroupTime - sceneStart) / scene.duration;
        return Math.min(1, Math.max(0, progress));
    };
    return words.map(({ word, startTime, endTime }) => ({
        word,
        startProgress: toProgress(startTime),
        endProgress: toProgress(endTime),
    }));
}

/**
 * The narration around a frame at scene progress `P`: the word that most recently finished (largest
 * endProgress ≤ P), the first word not yet started (smallest startProgress > P), and every word
 * between them inclusive. Clamps to the first/last word when the frame precedes or follows all
 * speech, so the window is never empty when the scene has words. `activeIndex` marks the word
 * actually being spoken at `P`, or -1 when `P` lands in a silence between words. Null only when
 * there are no words. Pure over the timed words.
 */
export function narrationWindowAt(timed: TimedWord[], progress: number): NarrationWindow | null {
    if (!timed.length) {
        return null;
    }
    let firstIndex: number | null = null;
    let firstEnd = -Infinity;
    let lastIndex: number | null = null;
    let lastStart = Infinity;
    timed.forEach((word, index) => {
        if (word.endProgress <= progress && word.endProgress > firstEnd) {
            firstEnd = word.endProgress;
            firstIndex = index;
        }
        if (word.startProgress > progress && word.startProgress < lastStart) {
            lastStart = word.startProgress;
            lastIndex = index;
        }
    });
    const start = firstIndex ?? 0;
    const end = lastIndex ?? timed.length - 1;
    const words = timed.slice(start, end + 1);
    const activeIndex = words.findIndex(
        (word) => word.startProgress <= progress && progress < word.endProgress,
    );
    return { words, activeIndex };
}

/**
 * Render a narration window as its words space-joined, wrapping the currently-spoken word in `**` so
 * the model can tell "now" from the flanking context. A silent gap (no active word) is left unmarked.
 */
export function formatNarrationWindow(window: NarrationWindow): string {
    return window.words
        .map((word, index) => (index === window.activeIndex ? `**${word.word}**` : word.word))
        .join(' ');
}

/**
 * Render timed narration as inline `[NN%] word` cues (one scene's worth), so the agent can read the
 * sentence and see exactly when each word lands as scene progress. Pure over the timed words.
 */
export function formatTimedNarration(
    words: ReadonlyArray<{ word: string; startProgress: number }>,
): string {
    const line = words
        .map(({ word, startProgress }) => `[${Math.round(startProgress * 100)}%] ${word}`)
        .join(' ');
    return `## Narration during this scene ([NN%] marks when each word is spoken as scene progress — sync your timeline to it)\n${line}`;
}

/**
 * Plain (untimed) narration, the fallback when word timings can't be mapped. Pure over the string;
 * returns '' when empty so the section is omitted.
 */
export function formatNarration(narration: string): string {
    const trimmed = narration.trim();
    if (!trimmed) {
        return '';
    }
    return `## Narration during this scene (the voiceover spoken while your graphic is on screen — design to complement it)\n"${trimmed}"`;
}

/** An asset with no area doesn't render, wherever it's positioned. `bounds` is "x y width height". */
function hasRenderableBounds(asset: AssetDescription): boolean {
    const [, , width, height] = asset.bounds.split(' ').map(Number);
    return !(width === 0 && height === 0);
}

/** Assets that don't render — zero-size, or text with no content — are noise for a designer. */
export function isVisibleAsset(asset: AssetDescription): boolean {
    if (!hasRenderableBounds(asset)) {
        return false;
    }
    if ('text' in asset && !(asset as TextAssetDescription).text) {
        return false;
    }
    return true;
}

/** Maps a template color's hex string to the `--l5-color-N` variable it's injected as. */
function paletteVarByColorHex(luminary: Luminary): Map<string, string> {
    const map = new Map<string, string>();
    (luminary.template?.colors ?? []).forEach((templateColor, index) => {
        const hex = intToHex(templateColor.color);
        if (!map.has(hex)) {
            map.set(hex, `${TEMPLATE_COLOR_VAR_PREFIX}${index}`);
        }
    });
    return map;
}

/** Serialized asset lines whose value is a color (so `#hex` tokens on them are colors). */
const COLOR_FIELD_LINE = /^\s*(?:color|colors|iconColors):/;

/**
 * Annotate `#hex` color mentions (with any trailing ` (name)` left in place) with the palette
 * variable carrying that color, so the agent references `var(--l5-color-N)` instead of hardcoding
 * the hex. Scoped to color-property lines only, so a `#hex` appearing in some other field (e.g. a
 * media description) is left untouched.
 */
export function annotatePaletteVars(text: string, varByColorHex: Map<string, string>): string {
    return text
        .split('\n')
        .map((line) => {
            if (!COLOR_FIELD_LINE.test(line)) {
                return line;
            }
            return line.replace(/#[0-9a-f]{6}(?: \([^)]*\))?/gi, (match) => {
                const hex = match.slice(0, 7).toLowerCase();
                const variable = varByColorHex.get(hex);
                return variable ? `${match} = var(${variable})` : match;
            });
        })
        .join('\n');
}

function formatNeighborSection(luminary: Luminary, scene: Scene, state: RootState): string {
    const index = scene.globalIndex;
    const neighbors: Array<{ label: string; index: number }> = [
        { label: 'previous', index: index - 1 },
        { label: 'next', index: index + 1 },
    ];

    const lines = neighbors
        .map(({ label, index: neighborIndex }) => {
            const neighbor = luminary.scenes[neighborIndex];
            if (!neighbor) {
                return null;
            }
            const description = getSceneDescriptions(neighbor, luminary, state);
            const summary = neighborSummary(neighbor, description.assets);
            return `- ${label} (scene index ${neighborIndex}): design ${description.design}, ${description.duration}${summary ? `, ${summary}` : ''}`;
        })
        .filter((line): line is string => line !== null);

    if (!lines.length) {
        return '';
    }
    return `## Neighboring scenes (for visual & narrative continuity)\n${lines.join('\n')}`;
}

/**
 * A neighbor's content for continuity: its narration (text only — timings are only worth their
 * tokens on the scene being authored) and its on-screen text, either of which may be absent. Both
 * are shown when present: the narration is what's spoken, the on-screen text is distinct copy the
 * designer's graphic sits beside.
 */
function neighborSummary(neighbor: Scene, assets: AssetDescription[]): string {
    const parts: string[] = [];
    const narration = neighbor.extractTranscript().text.trim();
    if (narration) {
        parts.push(`narration: "${narration}"`);
    }
    const text = neighborText(assets);
    if (text) {
        parts.push(`on-screen text: ${text}`);
    }
    return parts.join(', ');
}

/**
 * The on-screen text of a scene, joined for a compact neighbor summary. Fancy captions carry no
 * `text` field (their words are just the narration rendered on screen, already covered by the
 * narration line), so the `'text' in asset` guard already excludes them.
 */
function neighborText(assets: AssetDescription[]): string {
    return assets
        .filter((asset): asset is TextAssetDescription => 'text' in asset && Boolean(asset.text))
        .map((asset) => `"${asset.text}"`)
        .join(', ');
}

function formatBrandSection(state: RootState): string {
    const brandKitId = getBrandKitId(state);
    if (!brandKitId) {
        return '';
    }
    const colors = getBrandKitColorsInHex(state, brandKitId).toArray();
    if (!colors.length) {
        return '';
    }
    return `## Customer brand colors (from their brand kit)\n${colors.join(', ')}`;
}

/**
 * When the user has picked a Codeo brand for this scene, hand the html-design agent the brand's
 * *qualitative* guidance so the generated graphic feels on-brand.
 *
 * We deliberately do NOT reuse Codeo's full `buildWebsiteBlock` brief here. That brief is built for
 * the standalone Codeo agent, which has machinery this agent lacks, so parts of it would only fail
 * silently:
 *   - Colors & fonts (`brandTokens` + the brand palette/typefaces): this agent themes off the
 *     Luminary template's `var(--l5-color-N)` / `var(--l5-font-N)` — the only values Luminary
 *     declares in the composition and embeds into renders. Codeo's `--codeo-*` vars are never
 *     written to `:root` here, and brand fonts aren't embedded, so referencing them paints nothing
 *     / silently falls back and diverges between the editor preview and the rendered video.
 *   - Logo, motifs, icons, and frames: these resolve through Codeo's asset registry and its
 *     `add_icon` / `get_brand_frame` tools, none of which exist here (this agent only has
 *     write/edit/capture). Handing them over just invites `var(--asset-*)` references that render
 *     nothing, or hallucinated tool calls.
 * So we pass only narrative, design language, motion cues, and describedMotifs (geometry the agent
 * redraws itself), and tell it to keep colors/fonts on the template. Wiring real brand assets in is
 * a planned follow-up. Returns '' when no brand is selected or its analysis isn't a ready result.
 */
function formatCodeoBrandSection(state: RootState): string {
    const brand = selectSelectedBrand(state);
    const result = brand?.analysis_result as WebsiteAnalysisResult | null | undefined;
    if (!result || result.status !== 'ready') {
        return '';
    }
    const brief = JSON.stringify({
        url: result.url,
        narrative: result.narrative,
        ...(result.designLanguage ? { designLanguage: result.designLanguage } : {}),
        motionCues: result.motionCues,
        ...(result.describedMotifs?.length ? { describedMotifs: result.describedMotifs } : {}),
    });
    return [
        '## Selected brand — the customer picked this brand for this scene; make the graphic look like it belongs to them',
        "Use the brand brief below for tone and design language. Keep colors on the template palette (`var(--l5-color-N)`) and fonts on the template fonts (`var(--l5-font-N)`) — use the brand's `designLanguage` (thesis, shape language, color semantics, do’s/don’ts) to decide how to apply them.",
        `<brand_brief>\n${brief}\n</brand_brief>`,
    ].join('\n');
}
